/*
 * PercentageSticker
 * @module components
 * @flow
 */

import React from 'react';
import styled, { css } from 'styled-components/macro';
import { ifProp } from 'styled-tools';
import { FormattedMessage } from 'react-intl';

import { DataValue, Tooltip } from '~/components';
import { getFlaskColor, getFlaskBackgroundImage } from '~/helpers';

import messages from './messages';

export type PercentageStickerProps = {
  sign?: string,
  value?: number,
  variant?: string,
  tooltip?: React$Node,
  isReversed?: boolean,
  device?: $PropertyType<DeviceQuery, 'device'>,
};

const PercentageStickerWrapper = styled.div.attrs(() => ({
  className: 'react-percentage-sticker',
}))`
  display: flex;
  align-items: center;
  background-image: ${getFlaskBackgroundImage};
  border-radius: 50%;
  color: ${getFlaskColor};
  font-weight: bold;
  justify-content: center;

  ${ifProp(
    { device: 'desktop' },
    css`
      width: 50px;
      height: 50px;
      font-size: 18px;
    `,
    css`
      width: 32px;
      height: 32px;
      font-size: 14px !important;
    `,
  )}

  sub {
    font-size: 10.5px;
  }
`;

/**
 * PercentageSticker React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {string} [props.sign] - percent sign
 * @param {number} [props.value] - value
 * @param {string} [props.variant] - colors variant
 * @param {React$Node} [props.tooltip] - tooltip text
 * @param {boolean} [props.isReversed] - reversed color flag
 * @param {$PropertyType<DeviceQuery, 'device'>} [props.device] - device mode
 * @example <caption>Simple usage</caption>
 * <PercentageSticker />
 * @returns {React.Node<*>} React-component
 */
export const PercentageSticker = ({
  sign,
  value,
  variant,
  tooltip,
  isReversed,
  device,
}: PercentageStickerProps) => {
  const content = (
    <PercentageStickerWrapper
      variant={variant}
      isReversed={isReversed}
      device={device}
      testid="percentage-sticker"
    >
      <DataValue value={value} percents isEmptyVisible />
      <sub>{sign || <FormattedMessage {...messages.sign} />}</sub>
    </PercentageStickerWrapper>
  );

  return tooltip ? <Tooltip overlay={tooltip}>{content}</Tooltip> : content;
};

PercentageSticker.defaultProps = {
  value: 0,
  variant: 'one',
};
